//js/ui-interactions.js
import {
    formatTime,
    getTrackTitle,
    getTrackArtists,
    escapeHtml,
    createQualityBadgeHTML,
    deriveTrackQuality,
    positionMenu,
} from './utils.js';
import { sidePanelManager } from './side-panel.js';
import { downloadQualitySettings, contentBlockingSettings } from './storage.js';
import { db } from './db.js';
import { syncManager } from './accounts/pocketbase.js';
import { showNotification, downloadTracks } from './downloads.js';
import { trackSearchTabChange, trackOpenQueue } from './analytics.js';
import {
    SVG_CLOSE,
    SVG_BIN,
    SVG_HEART,
    SVG_DOWNLOAD,
    SVG_HEART_FILLED,
    SVG_SQUARE_PEN,
    SVG_TRASH,
    SVG_EQUAL,
} from './icons.js';
import { hapticSuccess } from './haptics.js';

export function initializeUIInteractions(player, api, ui) {
    const sidebar = document.querySelector('.sidebar');
    const sidebarOverlay = document.getElementById('sidebar-overlay');
    const hamburgerBtn = document.getElementById('hamburger-btn');
    const queueBtn = document.getElementById('queue-btn');
    const libraryPage = document.getElementById('page-library');

    if (libraryPage) {
        libraryPage.addEventListener('dragstart', (e) => {
            const playlistCard = e.target.closest('.card.user-playlist');
            if (playlistCard) {
                e.dataTransfer.setData('text/playlist-id', playlistCard.dataset.userPlaylistId);
                e.dataTransfer.effectAllowed = 'move';
            }
        });

        const handleDragOver = (e) => {
            const folderCard = e.target.closest('.card[data-folder-id]');
            if (folderCard && e.dataTransfer.types.includes('text/playlist-id')) {
                e.preventDefault();
                folderCard.classList.add('drag-over');
            }
        };

        const handleDragLeave = (e) => {
            const folderCard = e.target.closest('.card[data-folder-id]');
            if (folderCard) {
                folderCard.classList.remove('drag-over');
            }
        };

        const handleDrop = async (e) => {
            e.preventDefault();
            const folderCard = e.target.closest('.card[data-folder-id]');
            if (folderCard) {
                folderCard.classList.remove('drag-over');
                const playlistId = e.dataTransfer.getData('text/playlist-id');
                const folderId = folderCard.dataset.folderId;

                if (playlistId && folderId) {
                    const updatedFolder = await db.addPlaylistToFolder(folderId, playlistId);
                    syncManager.syncUserFolder(updatedFolder, 'update');
                    const subtitle = folderCard.querySelector('.card-subtitle');
                    if (subtitle) {
                        subtitle.textContent = `${updatedFolder.playlists.length} playlists`;
                    }
                    showNotification('Playlist added to folder');
                }
            }
        };

        libraryPage.addEventListener('dragover', handleDragOver);
        libraryPage.addEventListener('dragleave', handleDragLeave);
        libraryPage.addEventListener('drop', handleDrop);
    }

    let draggedQueueIndex = null;
    let queueStartIndex = 0;
    let queueEndIndex = 1000;
    let isQueueRendering = false;
    let topObserver = null;
    let bottomObserver = null;
    const QUEUE_VIRTUALIZATION_THRESHOLD = 1500;
    const QUEUE_MAX_RENDERED = 1000;
    const QUEUE_CHUNK_SIZE = 200;
    const ESTIMATED_ITEM_HEIGHT = 58;

    // Sidebar mobile
    hamburgerBtn.addEventListener('click', () => {
        sidebar.classList.add('is-open');
        sidebarOverlay.classList.add('is-visible');
    });

    const closeSidebar = () => {
        sidebar.classList.remove('is-open');
        sidebarOverlay.classList.remove('is-visible');
    };

    sidebarOverlay.addEventListener('click', closeSidebar);

    sidebar.addEventListener('click', (e) => {
        if (e.target.closest('a')) {
            closeSidebar();
        }
    });

    // Queue panel
    const renderQueueControls = (container) => {
        const currentQueue = player.getCurrentQueue();
        const showActionBtns = currentQueue.length > 0;

        container.innerHTML = `
            <button id="download-queue-btn" class="btn-icon" title="Download Queue" style="display: ${showActionBtns ? 'flex' : 'none'}">
                ${SVG_DOWNLOAD(20)}
            </button>
            <button id="like-queue-btn" class="btn-icon" title="Add Queue to Liked" style="display: ${showActionBtns ? 'flex' : 'none'}">
                ${SVG_HEART(20)}
            </button>
            <button id="add-queue-to-playlist-btn" class="btn-icon" title="Add Queue to Playlist" style="display: ${showActionBtns ? 'flex' : 'none'}">
                ${SVG_SQUARE_PEN(20)}
            </button>
            <button id="clear-queue-btn" class="btn-icon" title="Clear Queue" style="display: ${showActionBtns ? 'flex' : 'none'}">
                ${SVG_TRASH(20)}
            </button>
            <button id="close-side-panel-btn" class="btn-icon" title="Close">
                ${SVG_CLOSE(20)}
            </button>
        `;

        container.querySelector('#close-side-panel-btn').addEventListener('click', () => {
            sidePanelManager.close();
        });

        const downloadBtn = container.querySelector('#download-queue-btn');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', async () => {
                downloadTracks(currentQueue, api, downloadQualitySettings.getQuality());
            });
        }

        const likeBtn = container.querySelector('#like-queue-btn');
        if (likeBtn) {
            likeBtn.addEventListener('click', async () => {
                let addedCount = 0;
                for (const track of currentQueue) {
                    const wasAdded = await db.toggleFavorite('track', track);
                    if (wasAdded) {
                        syncManager.syncLibraryItem('track', track, true);
                        addedCount++;
                    }
                }

                if (addedCount > 0) {
                    showNotification(`Added ${addedCount} track${addedCount > 1 ? 's' : ''} to Liked`);
                } else {
                    showNotification('All tracks in queue are already liked');
                }

                refreshQueuePanel();
            });
        }

        const addToPlaylistBtn = container.querySelector('#add-queue-to-playlist-btn');
        if (addToPlaylistBtn) {
            addToPlaylistBtn.addEventListener('click', async () => {
                const playlists = await db.getPlaylists();
                if (playlists.length === 0) {
                    showNotification('No playlists yet. Create one first.');
                    return;
                }

                const modal = document.createElement('div');
                modal.className = 'modal active';
                modal.innerHTML = `
                    <div class="modal-overlay"></div>
                    <div class="modal-content">
                        <h3>Add Queue to Playlist</h3>
                        <div class="modal-list">
                            ${playlists
                                .map(
                                    (p) => `
                                <div class="modal-option" data-id="${p.id}">${escapeHtml(p.name)}</div>
                            `
                                )
                                .join('')}
                        </div>
                        <div class="modal-actions">
                            <button class="btn-secondary cancel-btn">Cancel</button>
                        </div>
                    </div>
                `;

                document.body.appendChild(modal);

                const closeModal = () => {
                    modal.remove();
                };

                modal.addEventListener('click', async (e) => {
                    if (e.target.classList.contains('modal-overlay') || e.target.classList.contains('cancel-btn')) {
                        closeModal();
                        return;
                    }

                    const option = e.target.closest('.modal-option');
                    if (option) {
                        const playlistId = option.dataset.id;
                        const playlistName = option.textContent;

                        try {
                            let addedCount = 0;
                            for (const track of currentQueue) {
                                await db.addTrackToPlaylist(playlistId, track);
                                addedCount++;
                            }

                            const updatedPlaylist = await db.getPlaylist(playlistId);
                            syncManager.syncUserPlaylist(updatedPlaylist, 'update');

                            showNotification(`Added ${addedCount} tracks to playlist: ${playlistName}`);
                        } catch (error) {
                            console.error('Failed to add tracks to playlist:', error);
                            showNotification('Failed to add tracks to playlist');
                        }

                        closeModal();
                    }
                });
            });
        }

        const clearBtn = container.querySelector('#clear-queue-btn');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                player.clearQueue();
                refreshQueuePanel();
            });
        }
    };

    const renderQueueItemHTML = (track, index, options = {}) => {
        const { isNowPlaying = false, section = 'context', sectionIndex = 0 } = options;
        const isBlocked = contentBlockingSettings?.shouldHideTrack(track);
        const isUnavailable = track?.isUnavailable;
        const trackTitle = getTrackTitle(track);
        const trackArtists = getTrackArtists(track, { fallback: 'Unknown' });
        const qualityBadge = createQualityBadgeHTML(track);
        const blockedTitle = isBlocked
            ? `title="Blocked: ${contentBlockingSettings.isTrackBlocked(track.id) ? 'Track blocked' : contentBlockingSettings.isArtistBlocked(track.artist?.id) ? 'Artist blocked' : 'Album blocked'}"`
            : '';

        const isVideo = track.type === 'video';
        const coverUrl =
            isVideo && track.imageId ? api.getVideoCoverUrl(track.imageId) : api.getCoverUrl(track.album?.cover);

        const isDraggable = section === 'manual' && !isBlocked;
        const isRemovable = section === 'manual' || section === 'autoplay';

        return `
        <div class="queue-track-item ${isNowPlaying ? 'playing' : ''} ${isBlocked ? 'blocked' : ''} ${isUnavailable ? 'unavailable' : ''}"
             data-queue-index="${index}" data-track-id="${track.id}" data-section="${section}" data-section-index="${sectionIndex}"
             draggable="${isDraggable}" ${blockedTitle}>
            ${isDraggable ? `<div class="drag-handle">${SVG_EQUAL(16)}</div>` : '<div class="drag-handle-spacer"></div>'}
            <div class="track-item-info">
                <img src="${coverUrl}"
                     class="track-item-cover" loading="lazy">
                <div class="track-item-details">
                    <div class="title">${escapeHtml(trackTitle)} ${qualityBadge}</div>
                    <div class="artist">${escapeHtml(trackArtists)}</div>
                </div>
            </div>
            <div class="track-item-duration">${isBlocked ? '--:--' : formatTime(track.duration)}</div>
            <button class="queue-like-btn" data-action="toggle-like" title="Add to Liked">
                ${SVG_HEART(20)}
            </button>
            ${isRemovable ? `<button class="queue-remove-btn" data-section="${section}" data-section-index="${sectionIndex}" title="Remove from queue">${SVG_BIN(20)}</button>` : ''}
        </div>
    `;
    };

    let draggedSection = null;
    let draggedSectionIndex = null;

    const attachQueueListeners = (container) => {
        if (container._queueListenersAttached) return;

        container.addEventListener('click', async (e) => {
            const item = e.target.closest('.queue-track-item');
            if (!item) return;

            const section = item.dataset.section;
            const sectionIndex = parseInt(item.dataset.sectionIndex);
            const queueIndex = parseInt(item.dataset.queueIndex);

            // Remove button
            const removeBtn = e.target.closest('.queue-remove-btn');
            if (removeBtn) {
                e.stopPropagation();
                const rmSection = removeBtn.dataset.section;
                const rmIndex = parseInt(removeBtn.dataset.sectionIndex);
                if (rmSection === 'manual') {
                    player.removeFromManualQueue(rmIndex);
                } else if (rmSection === 'autoplay') {
                    player.removeFromAutoplayQueue(rmIndex);
                } else {
                    player.removeFromQueue(queueIndex);
                }
                refreshQueuePanel();
                return;
            }

            // Like button
            const likeBtn = e.target.closest('.queue-like-btn');
            if (likeBtn && likeBtn.dataset.action === 'toggle-like') {
                e.stopPropagation();
                let track;
                if (section === 'manual') track = player.manualQueue[sectionIndex];
                else if (section === 'autoplay') track = player.autoplayQueue[sectionIndex];
                else track = player.getCurrentQueue()[queueIndex];

                if (track) {
                    const added = await db.toggleFavorite('track', track);
                    syncManager.syncLibraryItem('track', track, added);
                    likeBtn.classList.toggle('active', added);
                    likeBtn.innerHTML = added ? SVG_HEART_FILLED(20) : SVG_HEART(20);
                    hapticSuccess();
                    showNotification(added ? `Added to Liked: ${track.title}` : `Removed from Liked: ${track.title}`);
                }
                return;
            }

            if (item.classList.contains('blocked') || item.classList.contains('unavailable')) return;

            // Click to play
            if (section === 'context' || section === 'nowPlaying') {
                player.playAtIndex(queueIndex);
            }
            refreshQueuePanel();
        });

        container.addEventListener('contextmenu', async (e) => {
            const item = e.target.closest('.queue-track-item');
            if (!item) return;

            e.preventDefault();
            const section = item.dataset.section;
            const sectionIndex = parseInt(item.dataset.sectionIndex);
            const queueIndex = parseInt(item.dataset.queueIndex);

            let track;
            if (section === 'manual') track = player.manualQueue[sectionIndex];
            else if (section === 'autoplay') track = player.autoplayQueue[sectionIndex];
            else track = player.getCurrentQueue()[queueIndex];

            const contextMenu = document.getElementById('context-menu');
            if (contextMenu && track) {
                const isLiked = await db.isFavorite('track', track.id);
                const likeItem = contextMenu.querySelector('li[data-action="toggle-like"]');
                if (likeItem) likeItem.textContent = isLiked ? 'Unlike' : 'Like';

                const trackMixItem = contextMenu.querySelector('li[data-action="track-mix"]');
                if (trackMixItem) {
                    trackMixItem.style.display = track.mixes?.TRACK_MIX ? 'block' : 'none';
                }

                positionMenu(contextMenu, e.clientX, e.clientY);
                contextMenu._contextTrack = track;
            }
        });

        // Drag-and-drop (manual queue only)
        container.addEventListener('dragstart', (e) => {
            const item = e.target.closest('.queue-track-item');
            if (item && item.dataset.section === 'manual') {
                draggedSection = 'manual';
                draggedSectionIndex = parseInt(item.dataset.sectionIndex);
                item.style.opacity = '0.5';
            }
        });

        container.addEventListener('dragend', (e) => {
            const item = e.target.closest('.queue-track-item');
            if (item) item.style.opacity = '1';
            draggedSection = null;
            draggedSectionIndex = null;
        });

        container.addEventListener('dragover', (e) => {
            const item = e.target.closest('.queue-track-item');
            if (item && item.dataset.section === 'manual' && draggedSection === 'manual') {
                e.preventDefault();
            }
        });

        container.addEventListener('drop', (e) => {
            e.preventDefault();
            const item = e.target.closest('.queue-track-item');
            if (item && item.dataset.section === 'manual' && draggedSection === 'manual') {
                const toIndex = parseInt(item.dataset.sectionIndex);
                if (draggedSectionIndex !== null && draggedSectionIndex !== toIndex) {
                    player.moveInManualQueue(draggedSectionIndex, toIndex);
                    refreshQueuePanel();
                }
            }
        });

        container._queueListenersAttached = true;
    };

    const renderQueueContent = (container, isUpdate = false) => {
        const queueView = player.getQueueView();
        const currentQueue = player.getCurrentQueue();

        if (!queueView.nowPlaying && currentQueue.length === 0 && queueView.manualQueue.length === 0) {
            container.innerHTML = '<div class="placeholder-text">Queue is empty.</div>';
            if (topObserver) topObserver.disconnect();
            if (bottomObserver) bottomObserver.disconnect();
            return;
        }

        isQueueRendering = true;
        attachQueueListeners(container);

        let html = '<div class="queue-sections" style="padding: 0.5rem">';

        // Section 1: Now Playing
        if (queueView.nowPlaying) {
            html += '<div class="queue-section queue-now-playing">';
            html += '<div class="queue-section-label">Now Playing</div>';
            html += renderQueueItemHTML(queueView.nowPlaying, player.currentQueueIndex, {
                isNowPlaying: true,
                section: 'nowPlaying',
                sectionIndex: 0,
            });
            html += '</div>';
        }

        // Section 2: Next in Queue (manual queue)
        if (queueView.manualQueue.length > 0) {
            html += '<div class="queue-section queue-manual">';
            html += '<div class="queue-section-label">Next in Queue</div>';
            queueView.manualQueue.forEach((track, i) => {
                html += renderQueueItemHTML(track, -1, {
                    section: 'manual',
                    sectionIndex: i,
                });
            });
            html += '</div>';
        }

        // Section 3: Next Up from context
        if (queueView.contextUpcoming.length > 0) {
            const contextLabel = queueView.contextMeta
                ? `Next Up from ${escapeHtml(queueView.contextMeta.name || queueView.contextMeta.type || 'Queue')}`
                : 'Next Up';

            html += '<div class="queue-section queue-context">';
            html += `<div class="queue-section-label">${contextLabel}</div>`;

            // Use virtualization for large context queues
            const startIdx = player.currentQueueIndex + 1;
            const contextTracks = queueView.contextUpcoming;

            if (contextTracks.length > QUEUE_VIRTUALIZATION_THRESHOLD) {
                const visibleCount = Math.min(contextTracks.length, QUEUE_MAX_RENDERED);
                contextTracks.slice(0, visibleCount).forEach((track, i) => {
                    html += renderQueueItemHTML(track, startIdx + i, {
                        section: 'context',
                        sectionIndex: i,
                    });
                });
            } else {
                contextTracks.forEach((track, i) => {
                    html += renderQueueItemHTML(track, startIdx + i, {
                        section: 'context',
                        sectionIndex: i,
                    });
                });
            }
            html += '</div>';
        }

        // Section 4: Autoplay
        if (queueView.autoplayQueue.length > 0) {
            html += '<div class="queue-section queue-autoplay">';
            html += '<div class="queue-section-label">Autoplay</div>';
            queueView.autoplayQueue.forEach((track, i) => {
                html += renderQueueItemHTML(track, -1, {
                    section: 'autoplay',
                    sectionIndex: i,
                });
            });
            html += '</div>';
        }

        html += '</div>';
        container.innerHTML = html;

        if (topObserver) topObserver.disconnect();
        if (bottomObserver) bottomObserver.disconnect();

        // Async: update like button states
        container.querySelectorAll('.queue-track-item').forEach(async (item) => {
            const section = item.dataset.section;
            const sectionIndex = parseInt(item.dataset.sectionIndex);
            const queueIndex = parseInt(item.dataset.queueIndex);

            let track;
            if (section === 'manual') track = player.manualQueue[sectionIndex];
            else if (section === 'autoplay') track = player.autoplayQueue[sectionIndex];
            else track = currentQueue[queueIndex];

            const likeBtn = item.querySelector('.queue-like-btn');
            if (likeBtn && track) {
                const isLiked = await db.isFavorite('track', track.id);
                likeBtn.classList.toggle('active', isLiked);
                likeBtn.innerHTML = isLiked ? SVG_HEART_FILLED(20) : SVG_HEART(20);
            }
        });

        isQueueRendering = false;
    };

    const refreshQueuePanel = () => {
        sidePanelManager.refresh('queue', renderQueueControls, renderQueueContent, { noClear: true });
    };

    const openQueuePanel = () => {
        trackOpenQueue();
        sidePanelManager.open('queue', 'Queue', renderQueueControls, renderQueueContent);

        setTimeout(() => {
            const container = document.getElementById('side-panel-content');
            const playingItem = container?.querySelector('.queue-track-item.playing');
            if (playingItem) {
                playingItem.scrollIntoView({ block: 'center', behavior: 'auto' });
            }
        }, 100);
    };

    queueBtn.addEventListener('click', openQueuePanel);

    // Expose renderQueue for external updates (e.g. shuffle, add to queue)
    window.renderQueueFunction = () => {
        if (sidePanelManager.isActive('queue')) {
            refreshQueuePanel();
        }

        const overlay = document.getElementById('fullscreen-cover-overlay');
        if (overlay && getComputedStyle(overlay).display !== 'none') {
            ui.updateFullscreenMetadata(player.currentTrack, player.getNextTrack());
        }
    };

    const folderPage = document.getElementById('page-folder');
    if (folderPage) {
        folderPage.addEventListener('dragover', (e) => {
            if (e.dataTransfer.types.includes('text/playlist-id')) {
                e.preventDefault();
                folderPage.classList.add('drag-over-folder-page');
            }
        });
        folderPage.addEventListener('dragleave', () => {
            folderPage.classList.remove('drag-over-folder-page');
        });
        folderPage.addEventListener('drop', async (e) => {
            e.preventDefault();
            folderPage.classList.remove('drag-over-folder-page');
            const playlistId = e.dataTransfer.getData('text/playlist-id');
            const folderId = window.location.pathname.split('/')[2];
            if (playlistId && folderId) {
                try {
                    const updatedFolder = await db.addPlaylistToFolder(folderId, playlistId);
                    syncManager.syncUserFolder(updatedFolder, 'update');
                    window.dispatchEvent(new HashChangeEvent('hashchange'));
                    showNotification('Playlist added to folder');
                } catch (error) {
                    console.error('Failed to add playlist to folder:', error);
                    showNotification('Failed to add playlist to folder', 'error');
                }
            }
        });
    }

    // Search and Library tabs
    document.querySelectorAll('.search-tab').forEach((tab) => {
        tab.addEventListener('click', () => {
            const page = tab.closest('.page');
            if (!page) return;

            // Track tab change
            trackSearchTabChange(tab.dataset.tab);

            page.querySelectorAll('.search-tab').forEach((t) => t.classList.remove('active'));
            page.querySelectorAll('.search-tab-content').forEach((c) => c.classList.remove('active'));

            tab.classList.add('active');

            const prefix = page.id === 'page-library' ? 'library-tab-' : 'search-tab-';
            const contentId = `${prefix}${tab.dataset.tab}`;
            document.getElementById(contentId)?.classList.add('active');
        });
    });

    // HD quality filter toggle
    const hdFilter = document.getElementById('search-hd-filter');
    if (hdFilter) {
        hdFilter.addEventListener('click', () => {
            hdFilter.classList.toggle('active');
            const results = ui._searchResults;
            if (!results) return;

            const isHdActive = hdFilter.classList.contains('active');

            const tracksContainer = document.getElementById('search-tracks-container');
            const albumsContainer = document.getElementById('search-albums-container');

            if (tracksContainer) {
                const tracks = isHdActive
                    ? results.tracks.filter((t) => deriveTrackQuality(t) === 'HI_RES_LOSSLESS')
                    : results.tracks;
                if (tracks.length) {
                    ui.renderListWithTracks(tracksContainer, tracks, true);
                } else {
                    tracksContainer.innerHTML =
                        '<div class="placeholder-text">' +
                        (isHdActive ? 'No Hi-Res tracks found.' : 'No tracks found.') +
                        '</div>';
                }
            }

            if (albumsContainer) {
                const albums = isHdActive
                    ? results.albums.filter((a) => deriveTrackQuality(a) === 'HI_RES_LOSSLESS')
                    : results.albums;
                if (albums.length) {
                    albumsContainer.innerHTML = albums.map((album) => ui.createAlbumCardHTML(album)).join('');
                    albums.forEach((album) => {
                        const el = albumsContainer.querySelector(`[data-album-id="${album.id}"]`);
                        if (el) {
                            ui.updateLikeState(el, 'album', album.id);
                        }
                    });
                } else {
                    albumsContainer.innerHTML =
                        '<div class="placeholder-text">' +
                        (isHdActive ? 'No Hi-Res albums found.' : 'No albums found.') +
                        '</div>';
                }
            }
        });
    }

    // Settings tabs
    document.querySelectorAll('.settings-tab').forEach((tab) => {
        tab.addEventListener('click', () => {
            document.querySelectorAll('.settings-tab').forEach((t) => t.classList.remove('active'));
            document.querySelectorAll('.settings-tab-content').forEach((c) => c.classList.remove('active'));

            tab.classList.add('active');

            const contentId = `settings-tab-${tab.dataset.tab}`;
            document.getElementById(contentId)?.classList.add('active');

            // Save active tab
            import('./storage.js').then(({ settingsUiState }) => {
                settingsUiState.setActiveTab(tab.dataset.tab);
            });
        });
    });

    // Tooltip for truncated text (desktop hover only)
    const canUseHoverTooltips = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    let tooltipEl = null;

    if (canUseHoverTooltips) {
        tooltipEl = document.getElementById('custom-tooltip');
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'custom-tooltip';
            document.body.appendChild(tooltipEl);
        }

        const updateTooltipPosition = (e) => {
            const x = e.clientX + 15;
            const y = e.clientY + 15;

            // Prevent going off-screen
            const rect = tooltipEl.getBoundingClientRect();
            const winWidth = window.innerWidth;
            const winHeight = window.innerHeight;

            let finalX = x;
            let finalY = y;

            if (x + rect.width > winWidth) {
                finalX = e.clientX - rect.width - 10;
            }

            if (y + rect.height > winHeight) {
                finalY = e.clientY - rect.height - 10;
            }

            // Ensure it stays within viewport
            if (finalX < 5) finalX = 5;
            if (finalY < 5) finalY = 5;
            if (finalX + rect.width > winWidth - 5) finalX = winWidth - rect.width - 5;
            if (finalY + rect.height > winHeight - 5) finalY = winHeight - rect.height - 5;

            tooltipEl.style.transform = `translate(${finalX}px, ${finalY}px)`;
            // Reset top/left to 0 since we use transform
            tooltipEl.style.top = '0';
            tooltipEl.style.left = '0';
        };

        document.body.addEventListener('mouseover', (e) => {
            const selector =
                '.card-title, .card-subtitle, .track-item-details .title, .track-item-details .artist, .now-playing-bar .title, .now-playing-bar .artist, .now-playing-bar .album, .pinned-item-name';
            const target = e.target.closest(selector);

            if (target) {
                // Remove native title if present to avoid double tooltip
                if (target.hasAttribute('title')) {
                    target.removeAttribute('title');
                }

                if (target.scrollWidth > target.clientWidth) {
                    tooltipEl.innerHTML = target.innerHTML.trim();
                    tooltipEl.classList.add('visible');
                    updateTooltipPosition(e);

                    const moveHandler = (moveEvent) => {
                        updateTooltipPosition(moveEvent);
                    };

                    const outHandler = () => {
                        tooltipEl.classList.remove('visible');
                        target.removeEventListener('mousemove', moveHandler);
                        target.removeEventListener('mouseleave', outHandler);
                        target.removeEventListener('click', outHandler);
                    };

                    target.addEventListener('mousemove', moveHandler);
                    target.addEventListener('mouseleave', outHandler);
                    target.addEventListener('click', outHandler);
                }
            }
        });
    }

    // Hide tooltip and context menu on any click to be safe
    document.addEventListener('mousedown', (e) => {
        if (tooltipEl) {
            tooltipEl.classList.remove('visible');
        }

        const contextMenu = document.getElementById('context-menu');
        if (contextMenu && contextMenu.style.display === 'block' && !contextMenu.contains(e.target)) {
            contextMenu.style.display = 'none';
        }
    });
}
